// AppPwa.jsx（置き換え例）
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate ,useLocation} from 'react-router-dom';
import BookPwa from './pages/bookPwa.jsx';
import MyBookPwa from './pages/myBookPwa.jsx';
import AllBookListPwa from './pages/allBookListPwa.jsx';
import InstallGuide from './components/InstallGuide';
import AndroidInstall from './pages/AndroidInstall.jsx';
import { isStandalone } from './utils/pwaInstalled';

// 端末判定（iPhone / Android の PWA を許可する想定）
function useDevice() {
  return useMemo(() => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const looksLikeIpad =
      /iPad/i.test(ua) ||
      ((/Macintosh/i.test(ua) || navigator.platform === 'MacIntel') && navigator.maxTouchPoints > 1);
    const isIphone = /iPhone/i.test(ua) && !looksLikeIpad;
    const isIpad = looksLikeIpad;
    return { isAndroid, isIphone, isIpad };
  }, []);
}

// sw 登録だけ行う（FCM関連は無し）
async function registerPwaSw() {
  if (!('serviceWorker' in navigator)) return null;
  // 既存が無ければ登録
  const existing = await navigator.serviceWorker.getRegistration('/pwa/').catch(() => null);
  if (!existing) {
    await navigator.serviceWorker.register('/pwa/sw.js', { scope: '/pwa/' });
  }
  // ready（初回は時間がかかる可能性）
  return navigator.serviceWorker.ready.catch(() => null);
}


function useInstalled() {
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const onVis = () => setInstalled(isStandalone());
    const onInstalled = () => setInstalled(true);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);
  return installed;
}

function Guard({ children }) {
  const installed = useInstalled();
  const loc = useLocation();
  // 未インストールなら /install に統一誘導
  if (!installed && loc.pathname !== '/install') {
    return <Navigate to="/install" replace />;
  }
  // インストール済みで /install に来たら /book に戻す
  if (installed && loc.pathname === '/install') {
    return <Navigate to="/book" replace />;
  }
  return children;
}


export default function AppPwa() {
  const { isAndroid, isIphone } = useDevice();

  const [installed, setInstalled] = useState(
    !!window.navigator.standalone || !!window.matchMedia?.('(display-mode: standalone)').matches
  );
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // manifest 取り付け
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/pwa/manifest.webmanifest?v=20250921';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // beforeinstallprompt / appinstalled
  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // 非対応端末の扱い（ここで制御）
  // 例：iPhone と Android 端末だけ許可（iPad/デスクトップは非対応にするなら false に）
  const supported =
    (isIphone || isAndroid)
    && 'serviceWorker' in navigator
    && (window.isSecureContext || ['localhost','127.0.0.1'].includes(window.location.hostname));

  useEffect(() => {
    let cleanupUpdate;
    // 非対応端末：SW解除して終了（残したいならこのブロックは削除してOK）
    if (!supported) {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration('/pwa/').then((reg) => reg?.unregister?.());
      }
      return; // 以降の処理に進まない
    }

    // 早期エラーのロガー（開発時に便利）
    const onError = (e) => console.warn('[PWA][error]', e?.message, e?.error);
    const onRej   = (e) => console.warn('[PWA][unhandledrejection]', e?.reason);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRej);

    (async () => {
      // SW 登録は一度だけ
      await registerPwaSw();

      // ready とタイムアウトをレース
      const timeoutMs = 10000;
      const timer = new Promise((_, rej) => setTimeout(() => rej(new Error('sw-timeout')), timeoutMs));

      try {
        const reg = await Promise.race([navigator.serviceWorker.ready, timer]);
        // 開発用イベント（任意）
        window.dispatchEvent(new CustomEvent('pwa:sw-scope', { detail: reg?.scope || '—' }));
      } catch (e) {
        // 初回 attach が遅く controller が付かないケース：静かなリロードで追従
        if (String(e?.message) === 'sw-timeout') {
          const reg = await navigator.serviceWorker.getRegistration('/pwa/').catch(() => null);
          if (reg) {
            const oneShotReload = () => {
              navigator.serviceWorker.removeEventListener('controllerchange', oneShotReload);
              window.location.reload();
            };
            navigator.serviceWorker.addEventListener('controllerchange', oneShotReload);
          } else {
            // 本当に未登録のときのみ案内（本番は alert 消しても可）
            // alert('Service Worker が未登録です。/pwa を再読込してください。');
            console.warn('[PWA] SW not registered.');
          }
        } else {
          console.warn('[PWA] ready wait failed', e);
        }
      }
    })();

    // SW 更新検知 → 即反映（SkipWaiting）
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration('/pwa/').then((reg) => {
        if (!reg) return;
        const onUpdateFound = () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              reg.waiting?.postMessage?.('SKIP_WAITING');
              window.location.reload();
            }
          });
        };
        reg.addEventListener('updatefound', onUpdateFound);
        cleanupUpdate = () => reg.removeEventListener('updatefound', onUpdateFound);
      });
    }

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRej);
      if (cleanupUpdate) cleanupUpdate();
    };
  }, [supported]);

  // 未インストールなら各 OS の案内ページへ
  const needInstallAndroid = isAndroid && !installed && supported;
  const needInstallIOS     = isIphone  && !installed && supported;

  return (
    <BrowserRouter basename="/pwa">
      <Routes>
        <Route
          path="/"
          element={<Navigate to={(needInstallAndroid || needInstallIOS) ? '/install' : '/book'} replace />}
        />
        <Route
          path="/install"
          element={
            needInstallAndroid
              ? <AndroidInstall deferredPrompt={deferredPrompt} onInstalled={() => setInstalled(true)} />
              : needInstallIOS
                ? <InstallGuide platform="ios" deferredPrompt={null} onDidInstall={() => setInstalled(true)} />
                : <Navigate to="/book" replace />
          }
        />
        <Route path="/book" element={<Guard><BookPwa /></Guard>} />
        <Route path="/my" element={<Guard><MyBookPwa /></Guard>} />
        <Route path="/all" element={<Guard><AllBookListPwa /></Guard>} />
        <Route path="*" element={<Navigate to="/book" replace />} />
      </Routes>
    </BrowserRouter>
  );
}